import React, { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import StyleDropdown from "../components/StyleDropdown";

export default function BulkImageGenerationOverlay({
  isOpen,
  onClose,
  isGenerating,
  generatingMode = null,
  overwriteElementsCount = 0,
  missingElementsCount = 0,
  selectedStyleValue = null,
  coverOrientation = "landscape_4_3",
  interiorOrientation = "portrait_4_3",
  onGenerateMissingImages = () => {},
  onGenerateAllImages = () => {},
  onStyleChange = (value) => {},
  onCoverOrientationChange = (value) => {},
  onInteriorOrientationChange = (value) => {},
  pendingJobsCount = 0,
  completedJobsCount = 0,
  bulkTotal = 0,
  bulkSessionStartPendingCount = 0,
}) {
  if (!isOpen) return null;

  const GENERATION_COST = 5;

  return (
    <div
      className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Main Modal Container */}
      <div
        className="relative bg-white rounded-lg max-w-md w-full p-6 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-4 right-4 flex gap-2">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            aria-label="Close modal"
            title="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

            <div className="mt-2">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Generate Images
              </h2>

              <p className="text-sm text-gray-500 mb-6">
                Generating images will take some time. Please do not close
                your browser during this process.
              </p>

              {/* Image Generation Settings */}
              <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">
                      Image Generation Settings
                    </h3>

                    {/* Visual Style Selector */}
                    <StyleDropdown
                      selectedStyleValue={selectedStyleValue}
                      onStyleValueChange={onStyleChange}
                      label="Visual Style"
                      showDescription={true}
                    />

                    {/* Orientation Selectors */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Orientations
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Cover Image
                          </label>
                          <select
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                            value={coverOrientation}
                            onChange={(e) => onCoverOrientationChange(e.target.value)}
                          >
                            <option value="square_hd">Square HD</option>
                            <option value="square">Square</option>
                            <option value="portrait_4_3">Portrait 3:4</option>
                            <option value="portrait_16_9">Portrait 9:16</option>
                            <option value="landscape_4_3">Landscape 4:3</option>
                            <option value="landscape_16_9">Landscape 16:9</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Interior Image
                          </label>
                          <select
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                            value={interiorOrientation}
                            onChange={(e) => onInteriorOrientationChange(e.target.value)}
                          >
                            <option value="square_hd">Square HD</option>
                            <option value="square">Square</option>
                            <option value="portrait_4_3">Portrait 3:4</option>
                            <option value="portrait_16_9">Portrait 9:16</option>
                            <option value="landscape_4_3">Landscape 4:3</option>
                            <option value="landscape_16_9">Landscape 16:9</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Generation Buttons */}
                    <div className="space-y-3 pt-2">
                      {missingElementsCount > 0 && (
                        <button
                          onClick={onGenerateMissingImages}
                          disabled={missingElementsCount === 0 || (isGenerating && generatingMode === 'missing')}
                          className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white gap-2 ${
                            missingElementsCount === 0 || (isGenerating && generatingMode === 'missing')
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          }`}
                        >
                          {isGenerating && generatingMode === 'missing' && (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          )}
                          Generate Missing Images ({missingElementsCount}) -{" "}
                          {missingElementsCount * GENERATION_COST} Credits Total
                        </button>
                      )}
                      {overwriteElementsCount > 0 && (
                        <button
                          onClick={onGenerateAllImages}
                          disabled={overwriteElementsCount === 0 || (isGenerating && generatingMode === 'overwrite')}
                          className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white gap-2 ${
                            overwriteElementsCount === 0 || (isGenerating && generatingMode === 'overwrite')
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          }`}
                        >
                          {isGenerating && generatingMode === 'overwrite' && (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          )}
                          Overwrite Existing Images ({overwriteElementsCount}) -{" "}
                          {overwriteElementsCount * GENERATION_COST} Credits Total
                        </button>
                      )}
                    </div>
                  </div>
        </div>
      </div>
    </div>
  );
}
